import { convertData } from '@shared/innmaxLib/services'
import { momentToString, toMoment } from '@shared/utils/webHelper'
import moment from 'moment'
import { always, cond, equals, isNil, map, pathOr, prop } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAllSessions } from '../session'
import {
  AppBannerShelfStatus,
  AppUserStatusEnums,
  useCouponTypeEnum,
  useMissionStatusEnum,
  useMissionTypeEnum,
  useRewardTypeEnum,
  useWeeklyOptionsEnum,
} from './app.enum'
import {
  IChargingHistory,
  IPersonalNotification,
  IRewardPoint,
  ISystemNotification,
  ITask,
} from './app.type'

export const convertAppUserStatusStr = (status?: number) => {
  return cond([
    [isNil, () => ''],
    [() => status === AppUserStatusEnums.INACTIVE, () => '停用'],
    [() => status === AppUserStatusEnums.PAUSE, () => '暫停'],
    [() => status === AppUserStatusEnums.ACTIVE, () => '啟用'],
  ])(status)
}

export const convertAppUserAuthTypeStr = (authType?: string) => {
  return cond([
    [isNil, () => ''],
    [() => authType === 'f', () => 'facebook'],
    [() => authType === 'g', () => 'google+'],
    [() => authType === 'a', () => 'email'],
    [() => authType === 'l', () => 'Line'],
    [() => authType === 'w', () => 'Wechat'],
    [() => authType === 'p', () => '霹靂網'],
    [() => authType === 'b', () => '後台'],
    [() => authType === 'apple', () => 'apple'],
    [() => authType === 'bTop', () => '台數科後台'],
    [() => authType === 'top', () => '台數科機上盒'],
    [() => authType === 'sso', () => 'sso登入'],
  ])(authType)
}

export const convertTaskDetail = (data?: ITask) => {
  return !!data
    ? {
        ...data,
        typeStr: cond([
          [equals(0), always('每週')],
          [equals(1), always('每月')],
          [equals(3), always('一次性')],
          [equals(4), always('地點')],
        ])(data.type),
        statusStr: cond([
          [equals(0), always('待執行')],
          [equals(1), always('執行中')],
          [equals(2), always('已結束')],
        ])(data.status),
      }
    : data
}

export const useConvertIBanner = (res: any) => {
  const { t } = useTranslation()
  const {
    createAt,
    closedAt,
    repairTimeSec,
    reviewedAt,
    sessionName,
    shelfStatus,
  } = useConvertData()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...createAt(x),
            ...closedAt(x),
            ...repairTimeSec(x),
            ...reviewedAt(x),
            ...sessionName(x),
            ...shelfStatus(x),
            deviceTypeStr: x.deviceType && t(`device:${x.deviceType}`),
            ticketSourceStr: x.ticketSource && t(`repair:${x.ticketSource}`),
          }
        },
      })(res),
    [res]
  )

  return { dataSource }
}

export const useConvertIReserve = (res: any) => {
  const { t } = useTranslation()
  const {
    createAt,
    closedAt,
    repairTimeSec,
    reviewedAt,
    sessionName,
    shelfStatus,
  } = useConvertData()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...createAt(x),
            ...closedAt(x),
            ...repairTimeSec(x),
            ...reviewedAt(x),
            ...sessionName(x),
            ...shelfStatus(x),
            deviceTypeStr: x.deviceType && t(`device:${x.deviceType}`),
            ticketSourceStr: x.ticketSource && t(`repair:${x.ticketSource}`),
          }
        },
      })(res),
    [res]
  )

  return { dataSource }
}

/**
 * 共用各設備轉換資料格式的功能
 */
export const useConvertData = () => {
  const { byId } = useAllSessions()

  const createAt = (d: any) => ({
    createAt: toMoment(d.createAt), //報修時間
    createAtStr: momentToString(d.createAt, 'YYYY/MM/DD HH:mm:ss'),
  })

  const reviewedAt = (d: any) => ({
    reviewedAt: toMoment(d.reviewedAt),
    reviewedAtStr: momentToString(d.reviewedAt, 'YYYY/MM/DD HH:mm:ss'),
  })

  const closedAt = (d: any) => ({
    closedAt: toMoment(d.closedAt),
    closedAtStr: momentToString(d.closedAt, 'YYYY/MM/DD HH:mm:ss'),
  })

  const repairTimeSec = (d: any) => {
    return {
      repairTimeSec: d.repairTimeSec, //維修時間
      repairTimeSecStr: d?.repairTimeSec
        ? moment('00:00:00.000', 'hh:mm:ss.SSS')
            .add(d.repairTimeSec, 's')
            .format('mm:ss')
        : '',
    }
  }

  const sessionName = (d: any) =>
    d?.sessionId
      ? {
          sessionName: pathOr('', [d.sessionId, 'name'], byId),
        }
      : {}

  //   AppBannerShelfStatus
  // banner 上架狀態 0:未執行, 1:執行中, 2:已執行
  const shelfStatus = (d: any) => ({
    shelfStatus: d.status,
    shelfStatusStr:
      AppBannerShelfStatus.find(x => x.value === d.status)?.label || '',
  })

  return {
    createAt,
    closedAt,
    repairTimeSec,
    reviewedAt,
    sessionName,
    shelfStatus,
  }
}

export const convertPersonalNotification = (data?: IPersonalNotification) => {
  return !!data
    ? {
        ...data,
        situationStr: cond([[equals(0), always('充電結束')]])(data.situation),
        enableStr: data.enable ? '啟用' : '停用',
      }
    : data
}

export const convertSystemNotification = (d?: ISystemNotification) => {
  return !!d
    ? {
        ...d,
        sendTimeStr: isNil(d.sendTime)
          ? ''
          : d.sendTime === -1
          ? '立即發送'
          : moment(d.sendTime).format('YYYY/MM/DD HH:mm:ss'),
        sendTimeTypeStr:
          d?.sendTime === -1
            ? '即時發送'
            : isNil(d?.sendTime)
            ? '--'
            : `排程發送, ${moment(d.sendTime).format('YYYY/MM/DD HH:mm:ss')}`,
        statusStr: cond([
          [equals(0), always('已結束')],
          [equals(1), always('待執行')],
        ])(d.status),
      }
    : d
}

export const convertRewardPoint = (d?: IRewardPoint) => {
  return !!d
    ? {
        ...d,
        createAtStr: isNil(d.createAt)
          ? ''
          : moment(d.createAt).format('YYYY/MM/DD HH:mm'),

        typeStr: cond([
          [equals(0), always('任務得點')],
          [equals(1), always('充電折抵')],
          [equals(1), always('點數兌換')],
        ])(d.type),
      }
    : d
}

export const convertChargingHistory = (
  d?: IChargingHistory
): IChargingHistory | undefined => {
  const chargingType: string[] = []
  if (d?.chargingFee) {
    chargingType.push('充電')
  }
  if (d?.parkingFee) {
    chargingType.push('停車')
  }
  return !!d
    ? {
        ...d,
        parkingEndAtStr: isNil(d.parkingEndAt)
          ? ''
          : moment(d.parkingEndAt).format('YYYY/MM/DD HH:mm'),
        parkingStartAtStr: isNil(d.parkingStartAt)
          ? ''
          : moment(d.parkingStartAt).format('YYYY/MM/DD HH:mm'),
        chargingType,
      }
    : d
}

export const useConvertIMissionRewardList = (res: any) => {
  const { t } = useTranslation()
  // const { createAt, closedAt,sessionName } = useConvertData()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            locationList: map(prop('id'), x.locationList),
            missionStartStr: isNil(x.missionStart)
              ? ''
              : moment(x.missionStart).format('YYYY/MM/DD HH:mm'),
            missionEndStr: isNil(x.missionEnd)
              ? ''
              : moment(x.missionEnd).format('YYYY/MM/DD HH:mm'),
          }
        },
      })(res),
    [res]
  )

  return { dataSource }
}

export const useConvertIMissionReward = (res: any) => {
  const { t } = useTranslation()
  const { statusEnum: rewardTypeEnum, byId: rewardTypeById } =
    useRewardTypeEnum()

  const { statusEnum: missionTypeEnum, byId: missionTypeById } =
    useMissionTypeEnum()

  const { byId: weeklyOptionsById } = useWeeklyOptionsEnum()

  const { byId: missionStatusById } = useMissionStatusEnum()

  return React.useMemo(() => {
    if (!res) {
      return {}
    }

    const rewardType =
      res.type === 4 ? rewardTypeEnum.PLACE : rewardTypeEnum.TIME

    return {
      ...res,
      rewardType,
      activeStr: res.active ? '啟用' : '未啟用',
      statusStr: missionStatusById[res.status]?.label || null,
      rewardTypeStr: rewardTypeById[rewardType]?.label || null,
      typeStr: missionTypeById[res.type]?.label || null,
      locationList: map(prop('id'), res.locationList),
      periodDetail: {
        ...res.periodDetail,
        timeStart: moment(res?.periodDetail?.timeStart, 'HH:mm'),
        timeStartStr: res?.periodDetail?.timeStart,
        timeEnd: moment(res?.periodDetail?.timeEnd, 'HH:mm'),
        timeEndStr: res?.periodDetail?.timeEnd,
        dateStr:
          res.type === missionTypeEnum.WEEKLY
            ? `${weeklyOptionsById[res.periodDetail.dayStart]?.label} ${
                res?.periodDetail?.timeStart
              } 至  ${weeklyOptionsById[res.periodDetail.dayEnd]?.label} ${
                res?.periodDetail?.timeEnd
              }`
            : res.type === missionTypeEnum.MONTHLY
            ? `${res.periodDetail.dayStart}號 ${res?.periodDetail?.timeStart} 至 ${res.periodDetail.dayEnd}號 ${res?.periodDetail?.timeEnd}`
            : '',
      },
    }
  }, [res])
}

export const useConvertIRewardPointList = (res: any) => {
  const { t } = useTranslation()

  const { byId: couponTypeById } = useCouponTypeEnum()
  const { statusEnum } = useMissionStatusEnum()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            startAtStr: isNil(x.startAt)
              ? ''
              : moment(x.startAt).format('YYYY/MM/DD HH:mm'),
            endAtStr: isNil(x.endAt)
              ? ''
              : moment(x.endAt).format('YYYY/MM/DD HH:mm'),
            couponTypeStr: couponTypeById[x.couponType].label || '',
            status: x.active ? x.status : statusEnum.FINISHED,
          }
        },
      })(res),
    [res]
  )

  return { dataSource }
}

export const useConvertIRewardPoint = (x: any) => {
  const { t } = useTranslation()

  const { byId: couponTypeById } = useCouponTypeEnum()
  const { statusEnum } = useMissionStatusEnum()

  return React.useMemo(() => {
    if (!x) {
      return {}
    }

    return {
      ...x,
      activeStr: x.active ? '啟用' : '未啟用',
      couponTypeStr: couponTypeById[x.couponType].label || '',
      allowMultipleRedemptionStr: x.allowMultipleRedemption ? '是' : '否',
      status: x.active ? x.status : statusEnum.FINISHED,
    }
  }, [x])
}

export const useConvertIRewardPointCouponList = (x: any) => {
  const { t } = useTranslation()

  return React.useMemo(() => {
    if (!x) {
      return []
    }

    return [...x]
  }, [x])
}
